import React, { useState, useContext } from "react";
import Login from "./Login";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import {
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Hidden,
  LinearProgress,
  Paper,
} from "@mui/material";
import Header from "./Header";
import DynamicPage from "./common/DynamicPage";
import Order from "./order/screen/Order";
import BakerOnboarding from "./baker/screen/BakerOnboarding";
import BakerEditProfile from "./baker/screen/BakerEditProfile";
import BakerEditPhoto from "./baker/screen/BakerEditPhoto";
import Reroute from "./common/Redirect";
import GetPaid from "./order/screen/GetPaid";
import ResponsiveDrawer from "./common/component/ResponsiveDrawer";
import { THEME } from "./common/constants";
import BakerLoan from "./order/screen/BakerLoan";
import MyPlan from "./baker/screen/MyPlan";
import CreateAddOn from "./invoice/screen/CreateAddOn";
import MyNotifications from "./baker/screen/MyNotifications";
import Gallery from "./baker/screen/Gallery";
import LocalAtmOutlinedIcon from "@mui/icons-material/LocalAtmOutlined";
import LocalMallOutlinedIcon from "@mui/icons-material/LocalMallOutlined";
import { Link as RouterLink } from "react-router-dom";
import { UserContext } from "./context/UserContext";
import Dashboard from "./home/screen/Dashboard";
import OrderDashboard from "./baker/screen/OrderDashboard";
import ProductionTracker from "./baker/screen/ProductionTracker";
import ProductionTrackerItem from "./baker/screen/ProductionTrackerItem";
import MyReviews from "./baker/screen/MyReviews";
import Quotes from "./baker/screen/Quotes";
import SalesReport from "./report/screen/SalesReport";
import ItemSalesReport from "./report/screen/ItemSalesReport";
import TransactionReport from "./report/screen/TransactionReport";
import PayoutsReport from "./report/screen/PayoutsReport";
import PayoutDetails from "./report/screen/PayoutDetails";
import Redirect from "./common/component/Redirect";
import MyCalendar from "./baker/screen/MyCalendar";
import KitchenInstructionsEntry from "./order/screen/KitchenInstructionsEntry";
import OrderCancel from "./order/screen/OrderCancel";
import StandardCatalog from "./product/screen/StandardCatalog";
import ProductEntry from "./product/screen/ProductEntry";
import CustomerDetail from "./customer/screen/CustomerDetail";
import FulfillmentPlanner from "./planner/screen/FulfillmentPlanner";
import CreateOrder from "./order/screen/CreateOrder";
import KitchenDashboard from "./baker/screen/KitchenDashboard";
import TipsReport from "./report/screen/TipsReport";
import RefundsReport from "./report/screen/RefundsReport";
import EditQuote from "./baker/screen/EditQuote";
import MyStoreLocations from "./baker/screen/MyStoreLocations";
import MyStaff from "./baker/screen/MyStaff";
import ReleaseNotes from "./baker/screen/ReleaseNotes";
import POS from "./order/screen/POS";
import { getDataFromLocalStorage, isKDSMode } from "./common/util";
import LoginWithAuthCode from "./common/screen/LoginWithAuthCode";
import LoginWithCustomToken from "./common/screen/LoginWithCustomToken";
import Quote from "./quote/screen/Quote";
import Terms from "./baker/screen/Terms";
import EditOrder from "./order/screen/EditOrder.js";
import OrderTransactions from "./order/screen/OrderTransactions.js";
import Footer from "./common/Footer.js";
import ScrollToTop from "./common/component/ScrollToTop.js";
import DraftOrders from "./baker/screen/DraftOrders.js";
import Today from "./home/screen/Today.js";
import QuickCharge from "./order/screen/QuickCharge.js";
import RevenueRecognitionReport from "./report/screen/RevenueRecognitionReport.js";
import CashDrawerActivityReport from "./report/screen/CashDrawerActivityReport.js";
import EndOfDayReport from "./report/screen/EndOfDayReport.js";
import SalesSummaryReport from "./report/screen/SalesSummaryReport.js";
import CustomCatalog from "./product/screen/CustomCatalog.js";
import ProductionPlan from "./baker/screen/ProductionPlan.js";
import Inventory from "./product/screen/Inventory.js";
import ProductInventory from "./product/screen/ProductInventory.js";
import PerformanceReport from "./report/screen/PerformanceReport.js";
import Events from "./event/screen/Events.js";
import EventEntry from "./event/screen/EventEntry.js";
import { canAccessPath } from "./common/access-control.js";

const theme = createTheme(THEME);

export default function App(props) {
  const { loading, user } = useContext(UserContext);
  const location = useLocation();

  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const posMode = getDataFromLocalStorage("pos");
  const kdsMode = getDataFromLocalStorage("kds");

  // Admin handoff: must run before auth gate so token can establish the session
  if (location.pathname === "/auth/custom") {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LoginWithCustomToken />
      </ThemeProvider>
    );
  }

  if (loading) {
    return <LinearProgress />;
  } else if (user) {
    if (posMode && !getDataFromLocalStorage("pos-user")) {
      return (
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <LoginWithAuthCode />
        </ThemeProvider>
      );
    } else if (kdsMode && !getDataFromLocalStorage("kds-user")) {
      return (
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <LoginWithAuthCode />
        </ThemeProvider>
      );
    } else if (!canAccessPath(user, location.pathname)) {
      return <Navigate replace to="/dashboard" />;
    } else {
      return (
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <ScrollToTop />

          <div style={{ display: "flex" }}>
            {(user.profile.status === "onboard" || user.profile.status === "offboard") && (
              <ResponsiveDrawer
                open={mobileDrawerOpen}
                toggle={() => setMobileDrawerOpen(!mobileDrawerOpen)}
              />
            )}
            <main style={{ flexGrow: 1, minWidth: 0, overflowX: "hidden" }}>
              {(user.profile.status === "onboard" || user.profile.status === "offboard") && (
                <Header toggleMobileDrawer={() => setMobileDrawerOpen(!mobileDrawerOpen)} />
              )}
              <Box style={{ minHeight: "100vh" }}>
                <Routes>
                  <Route
                    path="/order/:orderId/kitchen/edit"
                    element={<KitchenInstructionsEntry />}
                  />
                  <Route path="/onboarding" element={<BakerOnboarding />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/create" element={<CreateOrder {...props} key={Date.now()} />} />
                  <Route
                    path="/quick-charge"
                    element={<QuickCharge {...props} key={Date.now()} />}
                  />
                  <Route path="/pos" element={<POS {...props} key={Date.now()} />} />
                  <Route path="/order/create" element={<CreateOrder />} />
                  <Route path="/order/draft" element={<DraftOrders />} />
                  <Route path="/order/:orderId/create-invoice" element={<CreateAddOn />} />
                  <Route path="/order/:orderId/get-paid" element={<GetPaid />} />
                  <Route path="/order/:orderId/mark-completed" element={<GetPaid />} />
                  <Route path="/order/:orderId/change" element={<EditOrder key={Date.now()} />} />
                  <Route path="/order/:orderId/cancel" element={<OrderCancel key={Date.now()} />} />
                  <Route path="/order/:orderId/transaction" element={<OrderTransactions />} />
                  <Route path="/order/:orderId" element={<Order key={Date.now()} />} />
                  <Route path="/my/order/:orderId" element={<Order key={Date.now()} />} />
                  <Route path="/order" element={<OrderDashboard key={Date.now()} />} />
                  <Route
                    path="/production/tracker/order/:orderId/item/:itemIndex"
                    element={<ProductionTrackerItem />}
                  />
                  <Route path="/production/tracker" element={<ProductionTracker />} />
                  <Route path="/quote/:reqId/edit" element={<EditQuote />} key={Date.now()} />
                  <Route path="/quote/:reqId" element={<Quote />} key={Date.now()} />
                  <Route path="/my/quote/:reqId" element={<Quote />} key={Date.now()} />
                  <Route path="/quote" element={<Quotes {...props} key={Date.now()} />} />
                  <Route path="/cash-advance" element={<BakerLoan />} />
                  <Route
                    path="/calendar"
                    element={
                      user.role === "kitchen" || user.role === "kitchen leader" ? (
                        <KitchenDashboard />
                      ) : (
                        <MyCalendar />
                      )
                    }
                  />
                  <Route path="/notification" element={<MyNotifications />} />
                  <Route path="/profile" element={<BakerEditProfile />} />
                  <Route path="/inventory/:productId" element={<ProductInventory />} />
                  <Route path="/inventory" element={<Inventory />} />
                  <Route path="/shop" element={<StandardCatalog />} />
                  <Route path="/shop/add" element={<ProductEntry />} />
                  <Route path="/shop/:productId" element={<ProductEntry />} />
                  <Route path="/catalog/custom" element={<CustomCatalog />} />
                  <Route path="/catalog/standard" element={<StandardCatalog />} />
                  <Route path="/catalog/standard/add" element={<ProductEntry />} />
                  <Route path="/catalog/standard/:productId" element={<ProductEntry />} />
                  <Route path="/events" element={<Events />} />
                  <Route path="/events/add" element={<EventEntry />} />
                  <Route path="/events/:eventId" element={<EventEntry />} />
                  <Route path="/customer/:customerId" element={<CustomerDetail />} />
                  <Route path="/settings/photos" element={<Gallery />} />
                  <Route path="/reviews" element={<MyReviews />} />
                  <Route path="/photo/:photoId/edit" element={<BakerEditPhoto />} />
                  <Route path="/report/sales" element={<SalesReport key={Date.now()} />} />
                  <Route path="/report/item-sales" element={<ItemSalesReport key={Date.now()} />} />
                  <Route
                    path="/report/transaction"
                    element={<TransactionReport key={Date.now()} />}
                  />
                  <Route
                    path="/report/cash-drawer-activity"
                    element={<CashDrawerActivityReport key={Date.now()} />}
                  />
                  <Route path="/report/end-of-day" element={<EndOfDayReport key={Date.now()} />} />
                  <Route
                    path="/report/sales-summary"
                    element={<SalesSummaryReport key={Date.now()} />}
                  />
                  <Route
                    path="/report/performance"
                    element={<PerformanceReport key={Date.now()} />}
                  />
                  <Route
                    path="/report/revenue-recognition"
                    element={<RevenueRecognitionReport key={Date.now()} />}
                  />
                  <Route path="/report/tips" element={<TipsReport key={Date.now()} />} />
                  <Route path="/report/refunds" element={<RefundsReport key={Date.now()} />} />
                  <Route path="/report/payout" element={<PayoutsReport key={Date.now()} />} />
                  <Route path="/payout/:payoutId" element={<PayoutDetails key={Date.now()} />} />
                  <Route path="/plan" element={<MyPlan />} />
                  <Route
                    path="/planning/fulfillment"
                    element={<FulfillmentPlanner key={Date.now()} />}
                  />
                  <Route path="/planner/production" element={<ProductionPlan key={Date.now()} />} />
                  <Route
                    path="/settings/store-location"
                    element={<MyStoreLocations key={Date.now()} />}
                  />
                  <Route path="/settings/staff" element={<MyStaff key={Date.now()} />} />
                  <Route path="/release-notes" element={<ReleaseNotes />} />
                  <Route path="/access-code" element={<LoginWithAuthCode />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/today" element={<Today />} />
                  <Route
                    path="/privacy-policy"
                    element={<DynamicPage {...props} handle="privacy-policy" />}
                  />
                  <Route
                    path="/terms-of-service"
                    element={<DynamicPage {...props} handle="terms-of-service" />}
                  />
                  <Route path="/to/:cat/:id" element={<Reroute />} />
                  <Route path="/redirect" element={<Redirect />} />
                  <Route path="/loan" element={<Navigate replace to="/cash-advance" />} />
                  <Route path="*" element={<Navigate replace to="/dashboard" />} />
                </Routes>
              </Box>
              <Footer />
              <Box my={10}></Box>
              {!isKDSMode(user) &&
                (canAccessPath(user, "/quote") || canAccessPath(user, "/order")) && (
                  <Hidden smUp>
                    <Paper sx={{ position: "fixed", bottom: 0, left: 0, right: 0 }} elevation={3}>
                      <BottomNavigation showLabels>
                        {canAccessPath(user, "/quote") && (
                          <BottomNavigationAction
                            label="Quotes"
                            icon={<LocalAtmOutlinedIcon />}
                            to={`/quote`}
                            component={RouterLink}
                          />
                        )}
                        {canAccessPath(user, "/order") && (
                          <BottomNavigationAction
                            label="Orders"
                            icon={<LocalMallOutlinedIcon />}
                            to={`/order`}
                            component={RouterLink}
                          />
                        )}
                      </BottomNavigation>
                    </Paper>
                  </Hidden>
                )}
            </main>
          </div>
        </ThemeProvider>
      );
    }
  } else {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Routes>
          <Route path="*" element={<Login />} />
        </Routes>
      </ThemeProvider>
    );
  }
}
